'use client';

import type { FC } from 'react';

import cn from 'classnames';
import { useSetAtom } from 'jotai';

import Button from '@gds/Button/Button';
import { openQuickFilterAtom } from '@plp/Atoms/QuickFilterOpenAtom';
import { MultiSelect } from '@plp/Components/MultiSelect/MultiSelect';

import type { FilterControlBaseProps } from '../../FilterPanel.entity';
import ApplyButton from '../ApplyButton/ApplyButton';
import SearchInput from '../SearchInput/SearchInput';
import useMakeFilter from './UseMakeFilter';

import styles from './MakeFilter.module.css';

type MakeFilterProps = FilterControlBaseProps;

const MakeFilter: FC<MakeFilterProps> = ({
  strings,
  triggerSearch,
  searchIsActive,
  isPlpFiltersSeoEnabled,
  tenant,
  applyButton,
}) => {
  const {
    allMakes,
    popularMakes,
    handleSelect,
    handleSearchChange,
    resetFilter,
    searchValue,
    selectedOptions: selectedMakes,
    filteredOptions: filteredMakes,
    handleApply,
  } = useMakeFilter(isPlpFiltersSeoEnabled, tenant, applyButton);

  const setOpenQuickFilter = useSetAtom(openQuickFilterAtom);

  const isSearching = !!searchValue;
  const makesContainerStyles = cn(
    styles.makes,
    {
      [styles.searching]: searchIsActive,
    },
    { [styles.applyButtonMakes]: applyButton },
  );

  const handleMakeApply = () => {
    handleApply();
    setOpenQuickFilter(null);
  };

  return (
    <div>
      <SearchInput
        searchValue={searchValue}
        isSearching={isSearching}
        handleSearchChange={handleSearchChange}
        placeholder={strings.searchPlaceholder}
        triggerSearch={triggerSearch!}
        searchIsActive={searchIsActive!}
        parentId="make"
      />
      <Button
        variant="link"
        onClick={resetFilter}
        disabled={selectedMakes.size === 0}
        size="small"
        className={styles.resetButton}
        dataTestId="reset-make-filter"
      >
        {strings.reset}
      </Button>
      <div className={makesContainerStyles}>
        {!isSearching && (
          <MultiSelect
            groupName="make"
            items={popularMakes}
            onSelect={handleSelect}
            title={strings.popularMakes}
            dataTestId="popular-makes"
            classes={{
              title: styles.popularMakesTitle,
            }}
            applyButton={applyButton}
            hideScrollbar={applyButton}
          />
        )}
        <MultiSelect
          groupName="make"
          items={searchValue ? filteredMakes : allMakes}
          onSelect={handleSelect}
          dataTestId="make-menu"
          title={isSearching ? '' : strings.allAvailableMakes}
          noResultText={strings.noResults}
          isGrouped
          groupBy="id"
          shouldOrder
          applyButton={applyButton}
          hideScrollbar={applyButton}
        />
      </div>
      {applyButton && <ApplyButton handleApply={handleMakeApply} ctaText={strings.apply} />}
    </div>
  );
};

export default MakeFilter;
